import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { can } from "@/lib/roles";
import logger from "@/lib/logger";

const FeatureFlagContext = createContext(null);

export function FeatureFlagProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState([]);
  const [devMode, setDevMode] = useState(false);
  const [allFlags, setAllFlags] = useState([]);

  // Load public feature flags for all users
  useEffect(() => {
    if (!user) return;
    api.getPublicFeatures().then(setFlags).catch((e) => logger.error("loadFeatureFlags:", e));
  }, [user]);

  // For admin: load all flags when dev mode is active
  useEffect(() => {
    if (!can(user, "edit_app_config") || !devMode) { setAllFlags([]); return; }
    api.adminGetFeatureFlags().then(setAllFlags).catch((e) => logger.error("loadAdminFlags:", e));
  }, [user, devMode]);

  const isAdmin = can(user, "edit_app_config");

  const toggleDevMode = useCallback(() => {
    if (!isAdmin) return;
    setDevMode((d) => !d);
  }, [isAdmin]);

  const hasFeature = useCallback((key) => {
    // In dev mode, admin sees all features
    if (devMode && isAdmin) {
      const flag = allFlags.find((f) => f.key === key);
      return flag ? flag.status !== "disabled" : false;
    }
    return flags.some((f) => f.key === key);
  }, [flags, allFlags, devMode, isAdmin]);

  const getFeatureStatus = useCallback((key) => {
    if (devMode && isAdmin) {
      const flag = allFlags.find((f) => f.key === key);
      return flag?.status || null;
    }
    const pub = flags.find((f) => f.key === key);
    return pub ? "public" : null;
  }, [flags, allFlags, devMode, isAdmin]);

  const refreshFlags = useCallback(async () => {
    try {
      const pub = await api.getPublicFeatures();
      setFlags(pub);
      if (devMode && isAdmin) {
        const all = await api.adminGetFeatureFlags();
        setAllFlags(all);
      }
    } catch (e) { logger.error("refreshFlags:", e); }
  }, [devMode, isAdmin]);

  // --- Split value into focused sub-memos (≤5 deps each) ---
  const flagState = useMemo(() => ({
    flags, allFlags, devMode, isAdmin,
  }), [flags, allFlags, devMode, isAdmin]);

  const flagActions = useMemo(() => ({
    toggleDevMode, hasFeature, getFeatureStatus, refreshFlags,
  }), [toggleDevMode, hasFeature, getFeatureStatus, refreshFlags]);

  const value = useMemo(() => ({
    ...flagState, ...flagActions,
  }), [flagState, flagActions]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error("useFeatureFlags must be used within FeatureFlagProvider");
  return ctx;
}
